import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PromoCodeInput from './PromoCodeInput';
import '../styles/GlobalDesign.css';
import PayPalCheckout from './PayPalCheckout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const PaymentForm = ({ resellerId = null }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('momo');
  const [formData, setFormData] = useState({
    nomclient: '',
    email: '',
    numeroSend: '',
    whatsappCode: '+221',
    whatsappNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aggregatorsConfig, setAggregatorsConfig] = useState(null);
  const [countriesCatalog, setCountriesCatalog] = useState([]);
  const [selectedAggregator, setSelectedAggregator] = useState(null);
  const [promoCode, setPromoCode] = useState(null);
  const [priceInfo, setPriceInfo] = useState({
    original: 0,
    discount: 0,
    final: 0
  });
  const planTemplates = [
    { id: 'premium',     name: 'Premium',      duration: '1 Mois',    price: '3500 FCFA',  priceUSD: '$6.50',  features: ['Toutes les chaînes', 'Qualité Full HD', '30 jours d\'accès', '1 appareil'] },
    { id: 'pro',         name: 'Pro',          duration: '3 Mois',    price: '7500 FCFA',  priceUSD: '$14.00', features: ['Toutes les chaînes', 'Qualité Full HD', '3 mois d\'accès', '1 appareil'] },
    { id: 'semi_annual', name: 'Semi-Annuel',  duration: '6 Mois',    price: '10500 FCFA', priceUSD: '$19.00', features: ['Toutes les chaînes', 'Qualité Full HD', '6 mois d\'accès', '2 appareils'] },
    { id: 'ultimate',    name: 'Ultimate',     duration: '1 An',      price: '15500 FCFA', priceUSD: '$28.00', features: ['Toutes les chaînes', 'Qualité Full HD', '1 an d\'accès', '2 appareils'], recommended: true }
  ];

  // Agrégateurs Mobile Money proposables (config admin) + catalogue pays.
  useEffect(() => {
    axios.get(`${API_URL}/payments/aggregators`)
      .then(({ data }) => {
        setAggregatorsConfig(data.aggregators || null);
        setCountriesCatalog(data.countries || []);
      })
      .catch(() => { /* non bloquant : on retombe sur le défaut serveur */ });
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/payments/public-plans`)
      .then(({ data }) => setPlans(planTemplates.filter(plan => data[plan.id]).map(plan => ({
        ...plan,
        name: data[plan.id].name,
        duration: `${data[plan.id].duration} jours`,
        price: `${data[plan.id].price} FCFA`,
        priceUSD: `$${data[plan.id].priceUSD}`
      }))))
      .catch(() => setError('Impossible de charger les tarifs'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setError('');
    setPromoCode(null);
    setPriceInfo({
      original: parseInt(plan.price.replace(/[^\d]/g, '')),
      discount: 0,
      final: parseInt(plan.price.replace(/[^\d]/g, ''))
    });
    
    // Scroll vers le formulaire de paiement
    setTimeout(() => {
      document.querySelector('.payment-form-container')?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePromoApplied = (promoData) => {
    if (promoData) {
      setPromoCode(promoData.code);
      setPriceInfo({
        original: promoData.originalPrice,
        discount: promoData.discountAmount,
        final: promoData.finalPrice
      });
    } else {
      setPromoCode(null);
      const planPrice = selectedPlan ? parseInt(selectedPlan.price.replace(/[^\d]/g, '')) : 0;
      setPriceInfo({
        original: planPrice,
        discount: 0,
        final: planPrice
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPlan) {
      setError('Veuillez sélectionner un plan');
      return;
    }

    if (!formData.nomclient || !formData.email || !formData.numeroSend || !formData.whatsappNumber) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!activeAggregator) {
      setError('Le paiement Mobile Money est momentanément indisponible pour ce pays. Essayez PayPal / Carte.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const paymentData = {
        plan_type: selectedPlan.id,
        nomclient: formData.nomclient,
        email: formData.email,
        numeroSend: formData.numeroSend,
        whatsappNumber: formData.whatsappCode + formData.whatsappNumber.replace(/\s/g, ''),
        aggregator: activeAggregator,
        ...(customerCountry && { country: customerCountry })
      };

      // Ajouter le code promo si présent
      if (promoCode) {
        paymentData.promo_code = promoCode;
      }

      // Ajouter le reseller_id si présent
      if (resellerId) {
        paymentData.reseller_id = resellerId;
      }

      const response = await axios.post(`${API_URL}/payments/initiate`, paymentData);

      if (response.data.success) {
        localStorage.setItem('payment_token', response.data.token);
        window.location.href = response.data.paymentUrl;
      } else {
        setError('Le paiement n\'a pas pu être initié');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erreur de paiement:', err);
      // Afficher le message du backend en priorité
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Erreur lors de l\'initiation du paiement';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const createPaypalOrder = async () => {
    if (!selectedPlan) throw new Error('Veuillez sélectionner un plan');
    if (!formData.nomclient || !formData.email || !formData.whatsappNumber) {
      throw new Error('Veuillez remplir le nom, l’email et WhatsApp');
    }
    const customerPhone = formData.whatsappCode + formData.whatsappNumber.replace(/\s/g, '');
    const response = await axios.post(`${API_URL}/payments/paypal/create-order`, {
        plan_type: selectedPlan.id,
        nomclient: formData.nomclient,
        email: formData.email,
        numeroSend: customerPhone,
        whatsappNumber: customerPhone,
        promo_code: promoCode || undefined,
        reseller_id: resellerId || undefined,
      });
    return response.data.orderId;
  };

  const capturePaypalOrder = async (orderId) => {
    const response = await axios.post(`${API_URL}/payments/paypal/capture-order`, { orderId });
    return response.data;
  };

  const countryCodes = [
    { code: '+221', country: 'Sénégal', flag: '🇸🇳' },
    { code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮' },
    { code: '+223', country: 'Mali', flag: '🇲🇱' },
    { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
    { code: '+229', country: 'Bénin', flag: '🇧🇯' },
    { code: '+228', country: 'Togo', flag: '🇹🇬' },
    { code: '+227', country: 'Niger', flag: '🇳🇪' },
    { code: '+224', country: 'Guinée', flag: '🇬🇳' },
    { code: '+241', country: 'Gabon', flag: '🇬🇦' },
    { code: '+242', country: 'Congo', flag: '🇨🇬' }
  ];

  const AGG_LABELS = {
    moneyfusion: { label: 'MoneyFusion', icon: '🟢' },
    leekpay:     { label: 'LeekPay',     icon: '🔵' },
  };

  // Pays du client déduit de l'indicatif WhatsApp choisi.
  const customerCountry = (countriesCatalog.find(c => c.dialCode === formData.whatsappCode) || {}).code || null;

  // Agrégateurs activés qui couvrent ce pays. Avant chargement de la config, on
  // laisse MoneyFusion par défaut (comportement historique).
  const availableAggregators = (() => {
    if (!aggregatorsConfig) return ['moneyfusion'];
    const enabled = ['moneyfusion', 'leekpay'].filter(n => aggregatorsConfig[n]?.enabled);
    if (!customerCountry) return enabled;
    const covering = enabled.filter(n => (aggregatorsConfig[n]?.countries || []).includes(customerCountry));
    return covering;
  })();

  const activeAggregator = availableAggregators.includes(selectedAggregator)
    ? selectedAggregator
    : (availableAggregators[0] || null);
  const momoUnavailable = paymentMethod === 'momo' && availableAggregators.length === 0;

  return (
    <div className={`payment-form-component${resellerId ? ' reseller-public-payment-form' : ''}`}>
      {paymentResult && (
        <div style={{ background: '#16A34A22', border: '1px solid #16A34A', borderRadius: '8px', padding: '20px', textAlign: 'center', marginBottom: '18px' }}>
          <p style={{ color: '#22C55E', fontWeight: '700', fontSize: '1rem' }}>Paiement confirmé</p>
          <p style={{ color: '#94A3B8', fontSize: '0.85rem' }}>Vos identifiants sont disponibles dans le reçu et seront aussi envoyés par email.</p>
          {(paymentResult.receiptDownloadUrl || paymentResult.receiptUrl) && (
            <a href={paymentResult.receiptDownloadUrl || paymentResult.receiptUrl} className="submit-button" style={{ display: 'block', textDecoration: 'none', marginTop: '14px' }}>
              Télécharger le reçu
            </a>
          )}
        </div>
      )}
      {/* Plans Grid */}
      <div className="plans-grid">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''} ${plan.popular ? 'popular' : ''} ${plan.recommended ? 'recommended' : ''}`}
            onClick={() => handlePlanSelect(plan)}
          >
            {plan.popular && <div className="badge popular-badge">⭐ Populaire</div>}
            {plan.recommended && <div className="badge recommended-badge">💎 Recommandé</div>}
            <h3 className="plan-name">{plan.name}</h3>
            <div className="plan-price">
              <div className="price-fcfa">{plan.price}</div>
              <div className="price-usd">{plan.priceUSD} USD</div>
            </div>
            <div className="plan-duration">{plan.duration}</div>
            <ul className="plan-features">
              {plan.features.map((feature, index) => (
                <li key={index}>✓ {feature}</li>
              ))}
            </ul>
            <button className="plan-select-btn" type="button">
              {selectedPlan?.id === plan.id ? '✓ Sélectionné' : 'Sélectionner'}
            </button>
          </div>
        ))}
      </div>

      {selectedPlan && (
        <div className="payment-form-container">
          {resellerId ? (
            <div className="reseller-payment-heading">
              <div>
                <span className="reseller-payment-step">Étape 2 · Paiement</span>
                <h2 className="form-title">Vos informations</h2>
              </div>
              <div className="reseller-selected-plan">
                <span>Offre sélectionnée · {selectedPlan.duration}</span>
                <strong>{selectedPlan.name}</strong>
                <b>{priceInfo.final.toLocaleString()} FCFA</b>
              </div>
            </div>
          ) : (
            <h2 className="form-title">Informations de paiement</h2>
          )}

          {/* Sélection méthode de paiement */}
          <div
            className="pm-toggle"
            style={resellerId ? undefined : { display: 'flex', gap: '10px', marginBottom: '20px' }}
          >
            <button type="button"
              className={`pm-btn ${paymentMethod === 'momo' ? 'pm-active' : ''}`}
              onClick={() => { setPaymentMethod('momo'); setError(''); }}
            >📱 Mobile Money</button>
            <button type="button"
              className={`pm-btn ${paymentMethod === 'paypal' ? 'pm-active' : ''}`}
              onClick={() => { setPaymentMethod('paypal'); setError(''); }}
            >PayPal / Carte</button>
          </div>

          {/* Champs communs */}
          <div className="form-grid payment-contact-grid" style={{ marginBottom: '16px' }}>
            <div className="form-group">
              <label>Nom complet *</label>
              <input type="text" name="nomclient" value={formData.nomclient} onChange={handleInputChange} placeholder="Votre nom complet" required />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="votre@email.com" required />
            </div>
            <div className="form-group">
              <label>WhatsApp (pour recevoir vos identifiants) *</label>
              <div className="phone-input-container">
                <select name="whatsappCode" value={formData.whatsappCode} onChange={handleInputChange} className="country-code-select">
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>{country.flag} {country.code}</option>
                  ))}
                </select>
                <input type="tel" name="whatsappNumber" value={formData.whatsappNumber} onChange={handleInputChange} placeholder="XX XXX XX XX" className="phone-number-input" required />
              </div>
            </div>
            {paymentMethod === 'momo' && (
              <div className="form-group">
                <label>Numéro Mobile Money *</label>
                <input type="tel" name="numeroSend" value={formData.numeroSend} onChange={handleInputChange} placeholder="+221 XX XXX XX XX" required />
              </div>
            )}
          </div>

          <PromoCodeInput
            planType={selectedPlan.id}
            planPrice={parseInt(selectedPlan.price.replace(/[^\d]/g, ''))}
            onPromoApplied={handlePromoApplied}
          />

          {priceInfo.discount > 0 && (
            <div className="price-breakdown">
              <div className="price-line original"><span>Prix original:</span><span className="strikethrough">{priceInfo.original} FCFA</span></div>
              <div className="price-line discount"><span>Réduction:</span><span className="discount-amount">-{priceInfo.discount} FCFA</span></div>
              <div className="price-line final"><span>Total à payer:</span><span className="final-amount">{priceInfo.final} FCFA</span></div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {paymentMethod === 'momo' ? (
            momoUnavailable ? (
              <div className="error-message" style={{ marginTop: 0 }}>
                Le paiement Mobile Money n'est pas disponible pour ce pays actuellement.
                Choisissez « PayPal / Carte » ci-dessus.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="payment-form" style={{ marginTop: 0 }}>
                {availableAggregators.length > 1 && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 8, color: '#94A3B8', fontSize: '0.85rem' }}>
                      Choisissez votre service de paiement
                    </label>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {availableAggregators.map(name => (
                        <button
                          key={name}
                          type="button"
                          className={`pm-btn ${activeAggregator === name ? 'pm-active' : ''}`}
                          onClick={() => setSelectedAggregator(name)}
                          style={{ flex: '1 1 140px' }}
                        >
                          {AGG_LABELS[name]?.icon} {AGG_LABELS[name]?.label || name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Traitement en cours...' : 'Procéder au paiement'}
                </button>
                <div className="security-note">🔒 Paiement sécurisé • Vos identifiants seront envoyés par email et WhatsApp</div>
              </form>
            )
          ) : !paymentResult && (
            <div className={resellerId ? 'reseller-paypal-checkout' : undefined} style={{ marginTop: '10px' }}>
              <PayPalCheckout key={selectedPlan.id} createOrder={createPaypalOrder}
                captureOrder={capturePaypalOrder} onSuccess={setPaymentResult}
                onError={err => setError(err.response?.data?.error || err.message || 'Paiement PayPal impossible')} />
              <div className="security-note">Paiement sécurisé via PayPal</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
